
import { Button, Modal, } from "react-bootstrap"
import { toast } from "react-toastify"
import { Storage } from "aws-amplify"
import { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

import { OPPORTUNITY_SUPPORTED_FILE_TYPES } from "../../../utils/constants"
import { useApiClient } from "../../../hooks/useApiClient"
import { OpportunityEntity } from "../../../api/models/AccountEntity"
import { randomString } from "../../../utils"
import SpinnerButton from "../../SpinnerButton/SpinnerButton"
import { VehicleEntity } from "../../../api"
import useSalesforceStore from "../../../store/SalesforceStore"
import { FileUploader } from "react-drag-drop-files"

type UploadFile = {
  file: File
  key: string
  filename: string
  fileType: string
}

type Props = {
  show: boolean
  opportunity?: OpportunityEntity
  vehicle?: VehicleEntity
  onHide: any
  labels?: { title: string, body: string, confirmBtn: string }
  onSuccess: () => void
  maxSize?: number;
  hasAllDocuments?: (files: UploadFile[]) => boolean | null | undefined;
}

const defaultLabels = {
  title: i18next.t('upload_document'),
  body: '',
  confirmBtn: i18next.t('confirm'),
}

const DocumentationFileUploaderModal = ({
  show,
  opportunity,
  onHide,
  onSuccess,
  labels = defaultLabels,
  hasAllDocuments,
  maxSize
}: Props) => {
  const { apiClient, handleApiResponse } = useApiClient();
  const [uploadFile, setUploadFile] = useState<File | null>(null)

  const [uploading, setUploading] = useState<boolean>(false)
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { t } = useTranslation()
  const { uploadCreditFileClicked } = useSalesforceStore();

  const uploadDocuments = async () => {
    if (!opportunity) {
      return
    }

    if (!uploadFile?.size) {
      return;
    }

    setUploading(true)
    let uploadOps: any[] = [];
    let filesToUploadList: UploadFile[] = [];

    const ext = uploadFile.name.split('.').pop();
    const key = `opportunity_uploads/${randomString(10)}.${ext}`;
    const fileType = uploadCreditFileClicked;

    filesToUploadList.push({ file: uploadFile, filename: uploadFile.name, key, fileType });

    uploadOps = filesToUploadList.map(obj => {
      return Storage.put(obj.key, obj.file, {
        contentType: obj.file.type,
        bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_SALESFORCE_IMPORT,
      });
    })

    try {
      await Promise.all(uploadOps)
    } catch (e) {
      console.error(e);
      toast.error(i18next.t('error_loading_file'))
      setUploading(false)
      return
    }

    const att = filesToUploadList.map(el => ({ ...el, key: `public/${el.key}` }))

    const isComplete = hasAllDocuments?.(att);
    const query = isComplete ? { complete: isComplete } : undefined;

    await handleApiResponse(apiClient
      .salesforceOpportunityController
      .addOpportunityAttachments(opportunity.Id, att, query),
      setUploading)

    toast.success(i18next.t('file_uploaded_success'))
    setUploadFile(null)
    onSuccess()
  }

  return (
    <Modal show={show} onHide={() => {
      setUploadFile(null)
      onHide()
    }} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{labels.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          {labels.body}
          <div>
            <FileUploader
              fileOrFiles={uploadFile}
              classes="dropzone"
              types={OPPORTUNITY_SUPPORTED_FILE_TYPES}
              handleChange={(file) => {
                if (typeof maxSize !== 'undefined' && file?.size > maxSize) {
                  toast.error(`Dimensione di caricamento massima superata (max ${maxSize / 1024 / 1024} MB)`);
                  return
                }
                setUploadFile(file);
              }}
              onTypeError={() => {
                setUploadFile(null);
                setUploadError(i18next.t('unsupported_file'));
              }}
              name="file"
            >
              <div className="text-center">
                <div className="fs-4">
                  <div>{t('click_or_drag_file')}</div>
                </div>
                <div>
                  <small>({OPPORTUNITY_SUPPORTED_FILE_TYPES.join(', ')})</small>
                </div>
              </div>

              <div className="text-center">
                {uploadFile && uploadFile?.size > 0 ? (
                  <ul className="mt-3">
                    <li key="name"> {uploadFile?.name} </li>
                  </ul>
                ) : (
                  ''
                )}
                {uploadError
                  ? <p className="mt-4 text-danger">{uploadError}</p>
                  : null
                }
              </div>
            </FileUploader>
            {uploadFile && uploadFile?.size > 0 ? (
              <div className="d-flex justify-content-end text-right">
                <Button
                  variant="danger"
                  onClick={() => {
                    setUploadFile(null);
                  }}
                  className="my-3 btn btn-sm btn-outline file-uploader-clear-btn"
                  type="button"
                >
                  {t('delete')}
                </Button>
              </div>
            ) : (
              ''
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={uploadDocuments} disabled={!uploadFile || uploading}>
          <SpinnerButton label={labels.confirmBtn} busy={uploading} />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DocumentationFileUploaderModal;

